import React from 'react';

import { AxisId, Debrief, Tracklog } from 'ff/debrief/models';
import { axisId, tracklogPointIndex } from 'ff/debrief/utils';
import {
    useGetDebriefByTrackUUIDQuery,
    useGetTracklogByIdQuery,
} from 'ff/debrief/features/TracklogApi';
import { useRouteContext } from 'ff/debrief/hooks';

export function useFlightDataDirectory() {
    const { selectedTracklogId } = useRouteContext();
    const { data: currentTracklog } = useGetTracklogByIdQuery(
        selectedTracklogId,
        { skip: !selectedTracklogId }
    );
    const { data: currentDebrief } = useGetDebriefByTrackUUIDQuery(
        selectedTracklogId,
        { skip: !selectedTracklogId }
    );

    const flightDataDirectory = React.useMemo(() => {
        return new FlightDataDirectory(currentTracklog, currentDebrief);
    }, [currentDebrief, currentTracklog]);

    return {
        flightDataDirectory,
    };
}

export class FlightDataDirectory {
    protected tracklog: Tracklog | undefined;
    protected debrief: Debrief | undefined;
    protected additionalSourceColumnHeaders: Set<string> = new Set();

    constructor(tracklog: Tracklog | undefined, debrief: Debrief | undefined) {
        this.tracklog = tracklog;
        this.debrief = debrief;
        for (const header of this.debrief?.additionalSourceColumns || []) {
            this.additionalSourceColumnHeaders.add(header.toUpperCase());
        }
    }

    /**
     * Indicates whether the data at the given index is present in the
     * tracklog. Note that this does not indicate that the data is
     * appropriate for display to the end user; see hasDisplayableDataForIndex().
     *
     * @param pointIndex
     */
    public hasDataForIndex(pointIndex: number): boolean {
        if (!this.tracklog) {
            return false;
        }
        return this.hasDataInFirstPoint(pointIndex);
    }

    /**
     * Indicates whether the data at the given index is appropriate for display to
     * the end user. Certain data, such as pitch, bank, and g-load, may be present in
     * the tracklog but not be appropriate for display if the source of the data is not
     * known.
     * @param pointIndex
     */
    public hasDisplayableDataForIndex(pointIndex: number): boolean {
        if (!this.hasDataForIndex(pointIndex)) {
            return false;
        }

        // If there is no debrief, all we have to go on is whether the data is present
        // in the tracklog, so we skip the "additional source header" checks.
        if (!this.debrief) {
            return true;
        }

        switch (pointIndex) {
            case tracklogPointIndex.pitchDeg:
                return this.additionalSourceColumnHeaders.has('DEG/PITCH');
            case tracklogPointIndex.bankDeg:
                return this.additionalSourceColumnHeaders.has('DEG/BANK');
            case tracklogPointIndex.gLoad:
                return this.additionalSourceColumnHeaders.has('G/NORMAC');
            default:
                return true;
        }
    }

    protected plottableFields = [
        {
            pointIndex: tracklogPointIndex.altitudeFeet,
            axisId: axisId.AltitudeAxis,
        },
        {
            pointIndex: tracklogPointIndex.aboveGroundLevel,
            axisId: axisId.AboveGroundLevelAxis,
        },
        {
            pointIndex: tracklogPointIndex.indicatedAirSpeed,
            axisId: axisId.AirspeedAxis,
        },
        {
            pointIndex: tracklogPointIndex.groundSpeed,
            axisId: axisId.GroundSpeedAxis,
        },
        {
            pointIndex: tracklogPointIndex.bankDeg,
            axisId: axisId.BankAxis,
        },
        {
            pointIndex: tracklogPointIndex.pitchDeg,
            axisId: axisId.PitchAxis,
        },
        {
            pointIndex: tracklogPointIndex.gLoad,
            axisId: axisId.GLoadAxis,
        },
    ];

    /**
     * Returns a list of AxisIds for which there is displayable data in the tracklog.
     */
    public getPlottableFields(): AxisId[] {
        if (!this.tracklog) return [];
        return this.plottableFields
            .filter(field => this.hasDisplayableDataForIndex(field.pointIndex))
            .map(field => field.axisId);
    }

    private hasDataInFirstPoint(pointIndex: number): boolean {
        const firstPoint = this.tracklog?.points?.data[0];
        if (
            !firstPoint ||
            !Array.isArray(firstPoint) ||
            firstPoint.length <= pointIndex
        ) {
            return false;
        }
        return (
            firstPoint[pointIndex] !== null &&
            firstPoint[pointIndex] !== undefined
        );
    }
}
